import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AdminService } from '../../../../service/admin.service';
import { Event } from '../../../../model/event';
import { UserEventRole } from '../../../../enum/user.event.role';
import { UserEvent } from '../../../../model/user.event';
import { FormGroup, FormBuilder } from '@angular/forms';
import { User } from 'src/app/model/user';
import { UserService } from 'src/app/service/user.service';
import { EventsService } from 'src/app/service/events.service';
import { NotificationService } from 'src/app/service/notification.service';
import * as dayjs from 'dayjs';
import { Sort } from '@angular/material/sort';
import { forkJoin } from 'rxjs';
import { ExportService } from 'src/app/service/export.service';
import { CountryPipe } from 'src/app/pipe/country.pipe';
import { TranslateService } from '@ngx-translate/core';
import { buildXlsxBlob, XlsxRow } from 'src/app/utils/xlsx-export';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-event-chairs',
  templateUrl: './event-chairs.component.html',
  styleUrls: ['./event-chairs.component.scss']
})
export class EventChairsComponent implements OnInit, OnChanges {
  // Chairs vêm via @Input do parent (event-members) — a chamada de network
  // é hoistada pra lá e roda junto com getMembers no init da página.
  // Quando ausente (uso standalone), o componente busca por conta própria.
  @Input() chairsInput: Array<UserEvent> | null = null;

  event: Event;
  chairs: Array<UserEvent>;
  filteredChairs: Array<UserEvent>;
  users: Array<User>;
  form: FormGroup;
  search: string;

  searchFoundUsers: Array<User>;
  userSignupForm: FormGroup;

  // Sort state — persistência via people-table (eventId + sortPersistKey="chairs").
  chairsSortState: Sort = { active: 'name', direction: 'asc' };

  // Atalho de copy sensível à plataforma — mesma fórmula do v2.
  public readonly copyShortcut: string =
    /Mac|iPhone|iPad|iPod/i.test((typeof navigator !== 'undefined' && (navigator.platform || navigator.userAgent)) || '')
      ? '⌘C' : 'Ctrl+C';

  constructor(
    private adminService: AdminService,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private userService: UserService,
    private eventsService: EventsService,
    public exportService: ExportService,
    private translateService: TranslateService,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
    setTimeout(() => {
      this.event = this.adminService.selectedEvent;
      if (!this.event) {
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
          this.bootstrapChairs();
        });
      } else {
        this.bootstrapChairs();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chairsInput'] && this.chairsInput && this.event) {
      this.setChairs(this.chairsInput);
      if (!this.form) { this.initForm(); }
    }
  }

  // Se já temos chairs via @Input (caso normal: parent event-members),
  // usa direto. Caso contrário (uso standalone), busca por conta própria.
  private bootstrapChairs(): void {
    if (this.chairsInput) {
      this.setChairs(this.chairsInput);
      this.initForm();
    } else {
      this.getChairs();
    }
  }

  public getChairs(): void {
    this.adminService.eventsService.getUserEventListByRole(this.event.id, UserEventRole.CHAIR).subscribe(chairs => {
      this.setChairs(chairs);
      this.initForm();
    });
  }

  public initForm(): void {
    this.form = this.fb.group({
      chairs: [this.chairs.map(chair => chair.user)]
    });

    this.form.get('chairs').valueChanges.subscribe((users: Array<User>) => {
      users.forEach(user => {
        if (!this.chairs.find(u => u.id === user.id)) {
          this.setUserAsChair(this.event, user);
          return users.splice(0, 1);
        }
      });
    });
  }

  private setChairs(chairs: Array<UserEvent>): void {
    this.chairs = chairs;
    this.filteredChairs = [...chairs];
    this.users = chairs.map(c => c.user);
    if (this.chairsSortState.active && this.chairsSortState.direction) {
      this.applyChairsSort();
    }
  }

  private pushChair(chair: UserEvent): void {
    this.chairs = this.chairs.concat([chair]);
    this.filteredChairs = [...this.chairs];
    this.users = this.chairs.map(c => c.user);
    if (this.chairsSortState.active && this.chairsSortState.direction) {
      this.applyChairsSort();
    }
  }

  setUserAsChair(event: Event, user: User): void {
    this.adminService.progressBar.start();
    this.notificationService.notify('admin.event.people.tpc.setting-user-as-chair', { params: { user: user.name } });
    this.adminService.eventsService.setUserAsAcceptedChair(event.id, user.id).subscribe(
      newChair => {
        this.adminService.progressBar.stop();
        this.pushChair(newChair);
        this.notificationService.notify('admin.event.people.tpc.set-user-as-chair', { params: { user: user.name } });
      },
      () => {
        this.adminService.progressBar.stop();
      }
    );
  }

  // ─── Sort ────────────────────────────────────────────────────────────

  public onChairsSortChange(sort: Sort): void {
    this.chairsSortState = sort;
    if (!sort.active || !sort.direction) {
      this.filteredChairs = [...(this.chairs || [])];
      return;
    }
    this.applyChairsSort();
  }

  private applyChairsSort(): void {
    const sort = this.chairsSortState;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = sort.active;
    const countryPipe = new CountryPipe(this.translateService);
    const value = (row: UserEvent): string | number => {
      switch (key) {
        case 'name': return row.user?.name?.toLowerCase() || '';
        case 'affiliation': {
          const a = row.user?.profile?.affiliation;
          return (a?.acronym || a?.name || '').toLowerCase();
        }
        case 'country': return (countryPipe.transform(row.user?.profile?.country) || '').toLowerCase();
        case 'email': return row.user?.email?.toLowerCase() || '';
        case 'notification-date': return row.notificationSentAt ? +new Date(row.notificationSentAt as any) : 0;
        default: return '';
      }
    };
    this.filteredChairs = [...this.filteredChairs].sort((a, b) => {
      const va = value(a); const vb = value(b);
      if (va < vb) { return -1 * dir; }
      if (va > vb) { return 1 * dir; }
      return 0;
    });
  }

  // ─── Ações por linha ─────────────────────────────────────────────────

  public sendNotificationOne(row: UserEvent): void {
    this.adminService.progressBar.start();
    this.notificationService.notify('admin.event.people.tpc.sending-notification-added-tpc-chair');
    this.eventsService.sendChairNotificationEmail(this.event.id, [row.user.id]).subscribe(() => {
      row.notificationSentAt = dayjs();
      this.adminService.progressBar.stop();
      this.notificationService.notify('admin.event.people.tpc.notification-sent');
    }, () => this.adminService.progressBar.stop());
  }

  public removeOneChair(row: UserEvent): void {
    this.adminService.progressBar.start();
    this.eventsService.deleteUserEvent(row.id).subscribe(() => {
      this.setChairs(this.chairs.filter(c => c.id !== row.id));
      this.adminService.progressBar.stop();
      this.notificationService.notify('admin.event.people.tpc.removed-user-as-chair', { params: { length: 1 } });
    }, () => this.adminService.progressBar.stop());
  }

  resendActivationEmail(user: User): void {
    this.notificationService.notify('admin.event.people.tpc.activation-email.sending', { params: { user: user.name } });
    this.userService.resendActivationEmail(user.email).subscribe(
      () => {
        this.notificationService.notify('admin.event.people.tpc.activation-email.sent', { params: { user: user.name } });
        this.adminService.progressBar.stop();
      },
      error => {
        if (error.status === 304) {
          this.notificationService.notify('errors.user-already-active');
        }
        this.adminService.progressBar.stop();
      }
    );
  }

  // ─── Ações em lote (floating bar) ───────────────────────────────────

  private selectedChairsRows(): Array<UserEvent> {
    return (this.chairs || []).filter(c => (c as any).selected);
  }

  public sendNotificationsSelected(): void {
    const rows = this.selectedChairsRows();
    if (!rows.length) { return; }
    const usersID = rows.map(r => r.user.id);
    this.adminService.progressBar.start();
    this.notificationService.notify('admin.event.people.tpc.sending-notification-added-tpc-chair');
    this.eventsService.sendChairNotificationEmail(this.event.id, usersID).subscribe(() => {
      rows.forEach(r => r.notificationSentAt = dayjs());
      this.adminService.progressBar.stop();
      this.notificationService.notify('admin.event.people.tpc.notification-sent');
    }, () => this.adminService.progressBar.stop());
  }

  // Download da lista de coordenadores como lista de e-mails (.txt) ou
  // planilha Excel (.xlsx). Mesmo padrão dos botões Baixar das outras
  // abas de Pessoas; reusa CountryPipe + buildXlsxBlob.
  public chairsDownloadEmails(): void {
    const list = this.chairs || [];
    if (!list.length) { return; }
    const emails = list.map(c => `"${c.user.name}" <${c.user.email}>`).join('\n');
    this.exportService.downloadFile(`${this.event.name}-chairs-emails.txt`, emails, 'text/plain');
  }

  public async chairsDownloadExcel(): Promise<void> {
    const list = this.chairs || [];
    if (!list.length) { return; }

    const countryPipe = new CountryPipe(this.translateService);

    const headers = [
      this.translateService.instant('forms.fields.name'),
      this.translateService.instant('forms.fields.email'),
      this.translateService.instant('forms.fields.affiliation'),
      this.translateService.instant('forms.fields.country'),
    ];

    const rows: XlsxRow[] = list.map(c => ({
      values: [
        c.user?.name ?? '',
        c.user?.email ?? '',
        c.user?.profile?.affiliation?.acronym ?? '',
        countryPipe.transform(c.user?.profile?.country) ?? '',
      ],
    }));

    const colWidths = [30, 32, 18, 14];
    const sheetName = this.translateService.instant('admin.event.people.members.tpc-chairs.title') || 'Coordenadores';
    const blob = await buildXlsxBlob(sheetName, headers, rows, [], colWidths);
    this.exportService.downloadBlob(`${this.event.name}-chairs.xlsx`, blob);
  }

  public removeChairsSelected(): void {
    const rows = this.selectedChairsRows();
    if (!rows.length) { return; }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.people.tpc.remove-selected.confirm.title',
        content: 'admin.event.people.tpc.remove-selected.confirm.content',
        cancel: 'forms.buttons.cancel',
        confirm: 'admin.event.people.tpc.row-actions.remove',
        destructive: true,
        params: { count: rows.length } as any,
      },
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.doRemoveChairs(rows);
    });
  }

  private doRemoveChairs(rows: UserEvent[]): void {
    const ids = rows.map(r => r.id);
    this.adminService.progressBar.start();
    forkJoin(ids.map(id => this.eventsService.deleteUserEvent(id))).subscribe(() => {
      this.setChairs(this.chairs.filter(c => !ids.includes(c.id)));
      this.adminService.progressBar.stop();
      const k = ids.length > 1
        ? 'admin.event.people.tpc.removed-users-as-chair'
        : 'admin.event.people.tpc.removed-user-as-chair';
      this.notificationService.notify(k, { params: { length: ids.length } });
    }, () => this.adminService.progressBar.stop());
  }

  // ─── Copy clipboard ─────────────────────────────────────────────────

  public async chairsCopySelected(): Promise<void> {
    const rows = this.selectedChairsRows();
    if (!rows.length) {
      this.notificationService.notify('submissions.ranking.copy.empty');
      return;
    }
    const html = this.buildChairsClipboardHtml(rows);
    const text = this.buildChairsClipboardText(rows);
    try {
      const ClipboardItemCtor = (window as any).ClipboardItem;
      if (navigator.clipboard && ClipboardItemCtor) {
        const item = new ClipboardItemCtor({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });
        await (navigator.clipboard as any).write([item]);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error('Clipboard API indisponível');
      }
      this.notificationService.notify('submissions.ranking.copy.success', { params: { count: rows.length } });
    } catch {
      this.notificationService.notifyError('submissions.ranking.copy.error');
    }
  }

  private buildChairsClipboardHtml(rows: Array<UserEvent>): string {
    const countryPipe = new CountryPipe(this.translateService);
    const t = (k: string) => this.translateService.instant(k);
    const esc = (s: string) => (s || '').replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as any)[c]);
    const fmtDate = (d: any) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—';
    const linkColor = (typeof document !== 'undefined'
      ? getComputedStyle(document.documentElement).getPropertyValue('--jems-link-color').trim()
      : '') || '#7C2D12';
    const FAMILY = 'Geist,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif';
    const ROW_BG = '#f1f0ef';
    const TEXT = '#373734';
    const rules: Array<[string, string]> = [
      ['table', `border-collapse:separate;border-spacing:1px 1px;width:auto;font-family:${FAMILY}`],
      ['th', `font-family:${FAMILY};font-size:12px;font-weight:500;line-height:16px;letter-spacing:0;padding:2px 8px;text-align:center;vertical-align:bottom;border:0;background:none;color:rgba(0,0,0,.54);white-space:nowrap`],
      ['td', `font-family:${FAMILY};font-size:13px;color:${TEXT};padding:7px 9px;border:0;vertical-align:middle;text-align:left;background-color:${ROW_BG}`],
      ['td.first-column', 'border-top-left-radius:6px;border-bottom-left-radius:6px'],
      ['td.last-column', 'border-top-right-radius:6px;border-bottom-right-radius:6px'],
      ['td.nowrap', 'white-space:nowrap'],
      ['a', `color:${linkColor};text-decoration:none !important`],
    ];
    const headers = [
      t('admin.event.people.tpc.table.name'),
      t('admin.event.people.tpc.table.affiliation'),
      t('admin.event.people.tpc.table.country'),
      t('admin.event.people.tpc.table.email'),
      t('admin.event.people.tpc.table.notification-date'),
    ];
    const headerRow = `<tr>${headers.map(h => `<th>${esc(h)}</th>`).join('')}</tr>`;
    const bodyRows = rows.map(r => {
      const name = r.user?.name || '';
      const affBase = r.user?.profile?.affiliation?.name || '';
      const acr = r.user?.profile?.affiliation?.acronym;
      const aff = acr ? (affBase ? `${acr} (${affBase})` : acr) : affBase;
      const country = countryPipe.transform(r.user?.profile?.country) || '';
      const email = r.user?.email || '';
      const emailHtml = email ? `<a href="mailto:${esc(email)}">${esc(email)}</a>` : '';
      const date = fmtDate(r.notificationSentAt);
      return `<tr>` +
        `<td class="first-column">${esc(name)}</td>` +
        `<td>${esc(aff)}</td>` +
        `<td>${esc(country)}</td>` +
        `<td>${emailHtml}</td>` +
        `<td class="last-column nowrap">${esc(date)}</td>` +
        `</tr>`;
    }).join('');
    let tableBlock = `<table><thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>`;
    tableBlock = this.inlineStyles(tableBlock, rules);
    return `<div style="font-family:${FAMILY};font-size:13px;color:${TEXT}">${tableBlock}</div>`;
  }

  private buildChairsClipboardText(rows: Array<UserEvent>): string {
    const countryPipe = new CountryPipe(this.translateService);
    const fmt = (d: any) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '';
    const clean = (s: string) => (s || '').replace(/\t/g, ' ').replace(/\r?\n/g, ' ').trim();
    return rows.map(r => {
      const affBase = r.user?.profile?.affiliation?.name || '';
      const acr = r.user?.profile?.affiliation?.acronym;
      const aff = acr ? (affBase ? `${acr} (${affBase})` : acr) : affBase;
      return [
        clean(r.user?.name || ''),
        clean(aff),
        clean(countryPipe.transform(r.user?.profile?.country) || ''),
        clean(r.user?.email || ''),
        clean(fmt(r.notificationSentAt)),
      ].join('\t');
    }).join('\n');
  }

  private inlineStyles(html: string, rules: Array<[string, string]>): string {
    const container = document.createElement('div');
    container.innerHTML = html;
    const append = (node: Element, decls: string) => {
      const prev = node.getAttribute('style') || '';
      const sep = prev && !prev.trim().endsWith(';') ? ';' : '';
      node.setAttribute('style', prev + sep + decls);
    };
    const orig = new Map<Element, string>();
    container.querySelectorAll('[style]').forEach(el => {
      orig.set(el, el.getAttribute('style') || '');
      el.removeAttribute('style');
    });
    for (const [selector, decls] of rules) {
      let nodes: NodeListOf<Element>;
      try { nodes = container.querySelectorAll(selector); } catch { continue; }
      nodes.forEach(node => append(node, decls));
    }
    orig.forEach((style, el) => append(el, style));
    return container.innerHTML;
  }

  // ─── Download (mantidos para futuro menu de download) ────────────────

  public exportChairToText() {
    const chairsEmail = this.chairs.map(m => `\"${m.user.name}\" <${m.user.email}>`).join('\n');
    this.exportService.downloadFile(`${this.event.name}-chairs.txt`, chairsEmail, 'text/plain');
  }

  public exportChairToCSV() {
    const countryPipe = new CountryPipe(this.translateService);
    const chairsCSV = this.chairs.map(m => `${m.user.name};${m.user.profile.affiliation.name};${countryPipe.transform(m.user.profile.country)};${m.user.email}`).join('\n');
    this.exportService.downloadFile(`${this.event.name}-chairs.csv`, chairsCSV, 'text/csv');
  }
}
